import type { ExpenseItem } from "@/services/APIs/PtsExpenseSummaryService";
import {
  CURRENCY_SYMBOLS,
  RECURRING_FREQUENCIES,
  USD_TO_INR_RATE,
  type ExpenseFilterValues,
} from "./constants";

export function parseAmount(value?: number | string | null): number {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
}

export function currencySymbol(currency?: string | null): string {
  return CURRENCY_SYMBOLS[String(currency ?? "").toUpperCase()] ?? "₹";
}

export function isInrCurrency(currency?: string | null): boolean {
  return String(currency ?? "").toUpperCase() === "INR";
}

/** Converts an amount to INR so mixed-currency rows can be totalled together. */
export function toInr(
  value?: number | string | null,
  currency?: string | null,
  rate = USD_TO_INR_RATE
): number {
  const amount = parseAmount(value);
  return isInrCurrency(currency) ? amount : amount * rate;
}

export function formatAmount(
  value?: number | string | null,
  fractionDigits = 2
): string {
  return parseAmount(value).toLocaleString("en-IN", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export function formatMoney(
  value: number | string | null | undefined,
  currency?: string | null,
  fractionDigits = 2
): string {
  return `${currencySymbol(currency)}${formatAmount(value, fractionDigits)}`;
}

export function formatInr(value?: number | string | null): string {
  return `₹${formatAmount(value, 0)}`;
}

/** API returns quantity as a decimal string ("1.00"); show it without padding. */
export function formatQuantity(value?: number | string | null): string {
  return String(parseAmount(value));
}

export function formatDateDisplay(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const raw = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  const [yyyy, mm, dd] = raw.split("-");
  const monthShort = new Date(Number(yyyy), Number(mm) - 1, 1).toLocaleString(
    "en-US",
    { month: "short" }
  );
  return `${dd}-${monthShort}-${yyyy}`;
}

export function toYmd(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function monthLabel(year: number, monthIndex: number): string {
  return new Date(year, monthIndex, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
}

export function monthShortLabel(year: number, monthIndex: number): string {
  return new Date(year, monthIndex, 1).toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  });
}

export function monthName(monthIndex: number): string {
  return new Date(2000, monthIndex, 1).toLocaleString("en-US", {
    month: "long",
  });
}

export function isInMonth(
  billingDate: string | null | undefined,
  year: number,
  monthIndex: number
): boolean {
  if (!billingDate) {
    return false;
  }
  const raw = String(billingDate).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return false;
  }
  const [y, m] = raw.split("-").map(Number);
  return y === year && m === monthIndex + 1;
}

export function isRecurring(frequency?: string | null): boolean {
  return RECURRING_FREQUENCIES.has(String(frequency ?? "").toLowerCase());
}

export function statusBadgeClass(status?: string | null): string {
  switch (String(status ?? "").toLowerCase()) {
    case "active":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "paused":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "cancelled":
    case "canceled":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
}

const CATEGORY_PALETTE = [
  "bg-violet-50 text-violet-700 border-violet-200",
  "bg-sky-50 text-sky-700 border-sky-200",
  "bg-emerald-50 text-emerald-700 border-emerald-200",
  "bg-amber-50 text-amber-700 border-amber-200",
  "bg-rose-50 text-rose-700 border-rose-200",
  "bg-teal-50 text-teal-700 border-teal-200",
  "bg-indigo-50 text-indigo-700 border-indigo-200",
];

const AVATAR_PALETTE = [
  "bg-violet-100 text-violet-700",
  "bg-sky-100 text-sky-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
];

function paletteIndex(value: string, size: number): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 100000;
  }
  return hash % size;
}

export function categoryBadgeClass(category?: string | null): string {
  const key = String(category ?? "").toLowerCase();
  if (!key) {
    return "bg-gray-100 text-gray-700 border-gray-200";
  }
  return CATEGORY_PALETTE[paletteIndex(key, CATEGORY_PALETTE.length)];
}

export function avatarClass(title?: string | null): string {
  const key = String(title ?? "").toLowerCase();
  if (!key) {
    return "bg-gray-100 text-gray-700";
  }
  return AVATAR_PALETTE[paletteIndex(key, AVATAR_PALETTE.length)];
}

export function initialOf(title?: string | null): string {
  const trimmed = String(title ?? "").trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
}

export function filterExpenses(
  items: ExpenseItem[],
  filters: ExpenseFilterValues
): ExpenseItem[] {
  const search = filters.search.trim().toLowerCase();

  return items.filter(item => {
    if (filters.category && item.category !== filters.category) {
      return false;
    }
    if (filters.frequency && item.frequency !== filters.frequency) {
      return false;
    }
    if (filters.status && item.status !== filters.status) {
      return false;
    }
    if (search) {
      const haystack = [item.title, item.category, item.vendor]
        .map(value => String(value ?? "").toLowerCase())
        .join(" ");
      if (!haystack.includes(search)) {
        return false;
      }
    }
    return true;
  });
}

export interface CategoryTotal {
  category: string;
  amountInr: number;
  percentage: number;
}

export interface ExpenseSummaryTotals {
  totalInr: number;
  recurringInr: number;
  oneTimeInr: number;
  recurringPercentage: number;
  oneTimePercentage: number;
  topCategories: CategoryTotal[];
}

export function computeExpenseSummary(
  items: ExpenseItem[],
  topCategoryCount = 2,
  rate = USD_TO_INR_RATE
): ExpenseSummaryTotals {
  let totalInr = 0;
  let recurringInr = 0;
  const byCategory = new Map<string, number>();

  items.forEach(item => {
    const amount = toInr(item.total_expense, item.currency, rate);
    totalInr += amount;
    if (isRecurring(item.frequency)) {
      recurringInr += amount;
    }
    const category = item.category?.trim() || "Uncategorized";
    byCategory.set(category, (byCategory.get(category) ?? 0) + amount);
  });

  const percentOf = (amount: number) =>
    totalInr > 0 ? (amount / totalInr) * 100 : 0;

  const topCategories = [...byCategory.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, topCategoryCount)
    .map(([category, amountInr]) => ({
      category,
      amountInr,
      percentage: percentOf(amountInr),
    }));

  const oneTimeInr = totalInr - recurringInr;

  return {
    totalInr,
    recurringInr,
    oneTimeInr,
    recurringPercentage: percentOf(recurringInr),
    oneTimePercentage: percentOf(oneTimeInr),
    topCategories,
  };
}

export function uniqueSorted(values: (string | null | undefined)[]): string[] {
  return [
    ...new Set(values.map(value => String(value ?? "").trim()).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));
}
